import * as _ from "lodash";
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import {
    Organization,
    OrganizationAffiliation,
    OrganizationAffiliationData,
    OrganizationAffiliationToData,
    OrganizationDetail,
    SearchOrganization
} from "../types";
import { GatewayBase } from "../bases";
import {
    OrganizationLocationData,
    OrganizationLocationToData,
    OrganizationRelation,
    OrganizationRelations,
    OrganizationRelationsData
} from "../types/organization-relation";
import { map, tap } from "rxjs/operators";
import { StorageService } from "../services/storage.service";

@Injectable({ providedIn: "root" })
export class OrganizationGateway extends GatewayBase {
    constructor(private _httpClient: HttpClient, private _storageService: StorageService) {
        super();
    }

    getOrganizationOverview(): Observable<Organization[]> {
        return this._httpClient
            .get<Organization[]>(`/api/v1.0/organizations-overview/get-grid`)
            .pipe(
                tap(orgs => {
                    this._storageService._populateNames(orgs);
                })
            );
    }

    updateOrganizationStatus(organizationId: number, isActive: boolean): Observable<any> {
        return this._httpClient.post(`/api/v1.0/organizations-overview/update-organization`, {
            id: organizationId,
            isActive
        });
    }

    getOrganization(id: number): Observable<OrganizationDetail> {
        return this._httpClient.get<OrganizationDetail>(
            `/api/v1.0/organization-view/organization/${id}`
        );
    }

    createOrganization(organization: OrganizationDetail): Observable<number> {
        return this._httpClient.post<number>(
            `/api/v1.0/organization-view/organization`,
            organization
        );
    }

    updateOrganization(organization: OrganizationDetail): Observable<OrganizationDetail> {
        return this._httpClient.put<OrganizationDetail>(
            `/api/v1.0/organization-view/organization`,
            organization
        );
    }

    organizationSearch(
        countryCode?: string,
        regulationCode?: string,
        companyNumber?: string
    ): Observable<SearchOrganization[]> {
        const params = { countryCode, regulationCode, companyNumber };
        if (!countryCode) {
            delete params.countryCode;
        }
        if (!regulationCode) {
            delete params.regulationCode;
        }
        if (!companyNumber) {
            delete params.companyNumber;
        }

        return this._httpClient.get<SearchOrganization[]>(
            "/api/v1.0/organization-view/organizations",
            { params }
        );
    }

    getRelations(organizationId: number): Observable<OrganizationRelations> {
        return this._httpClient
            .get<OrganizationRelationsData>(
                `/api/v1.0/organization-view/${organizationId}/organization-relations/hierarchies`
            )
            .pipe(map(res => new OrganizationRelations(res)));
    }

    saveRelations(relation: OrganizationRelation): Observable<any> {
        const locations: OrganizationLocationData[] = relation.locations.map(loc =>
            OrganizationLocationToData(loc)
        );

        return this._httpClient.post(
            `/api/v1.0/organization-view/${relation.parentId}/organization-relations/hierarchies`,
            locations
        );
    }

    getAffiliations(organizationId?: number): Observable<OrganizationAffiliation[]> {
        const params =
            organizationId == null ? null : { organizationId: organizationId.toString() };

        return this._httpClient
            .get(`/api/v1.0/affiliation-view`, { params })
            .pipe(
                map((res: OrganizationAffiliationData[]) =>
                    res.map(x => new OrganizationAffiliation(x))
                )
            );
    }

    createAffiliation(affiliation: OrganizationAffiliation): Observable<any> {
        const affiliationData = OrganizationAffiliationToData(affiliation);
        return this._httpClient.post(`/api/v1.0/affiliation-view`, affiliationData);
    }

    updateAffiliation(affiliation: OrganizationAffiliation): Observable<any> {
        const affiliationData = OrganizationAffiliationToData(affiliation);
        return this._httpClient.put(
            `/api/v1.0/affiliation-view/${affiliation.id}`,
            affiliationData
        );
    }

    getOrganizationFromVektis(agbCode: string): Observable<OrganizationDetail> {
        return this._httpClient.get<OrganizationDetail>(
            `api/v1.0/organization-view/organization/vektis/${agbCode}`
        );
    }

    deleteOrganization(organizationId: number): Observable<void> {
        return this._httpClient.delete<void>(
            `api/v1.0/organization-view/organization/${organizationId}`
        );
    }
}
